import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, Lock, Trash2, Shield } from 'lucide-react';
import image5 from '../images/image-5.png';
import { getStoreUrl } from '../utils/storeUrls';

const FeatureSections = () => {
  const { t, i18n } = useTranslation();
  
  return (
    <div className="max-w-6xl mx-auto px-6 py-16 bg-white dark:bg-slate-900">
      {/* Audio Format Section */}
      <div className="grid md:grid-cols-2 gap-12 items-center mb-32">
        <div className="relative">
          <div className="bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={image5}
              alt="Person using audio features"
              className="w-full"
            />
          </div>
          <div className="absolute top-1/3 right-0 md:translate-x-1/4 bg-primary text-white p-4 rounded-lg">
            <div className="grid grid-cols-3 gap-3">
              {['🎤', '🌐', '📝', '🔊', '📱', '↗️'].map((emoji, i) => (
                <span key={i} className="text-xl">{emoji}</span>
              ))}
            </div>
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-white p-4 rounded-lg shadow-lg w-3/4">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">📊</span>
              <div>
                <div className="font-medium text-gray-600">Market Analysis</div>
                <div className="text-sm text-gray-500">8/25/24 • 50 min, 29 sec</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="text-primary font-medium mb-2">{t('featureSections.audioFormat.label')}</div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4 dark:text-white">
            {t('featureSections.audioFormat.title')}
          </h2>
          <p className="text-gray-600 mb-6 dark:text-gray-400">
            {t('featureSections.audioFormat.description')}
          </p>
          <div className="space-y-3 mb-12">
            {(Array.isArray(t('featureSections.audioFormat.features', { returnObjects: true }))
              ? t('featureSections.audioFormat.features', { returnObjects: true }) as string[]
              : [
                  "Instant AI transcription with 99% accuracy",
                  "Smart summarization and key point extraction",
                  "YouTube video summarization capability",
                ]
            ).map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700 text-sm dark:text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
          <a
            href={getStoreUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition-colors"
          >
            {t('featureSections.downloadNow')}
          </a>
        </div>
      </div>

      {/* Privacy Section */}
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-primary font-medium mb-2">{t('featureSections.privacy.label')}</div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4 dark:text-white">
            {t('featureSections.privacy.title')}
          </h2>
          <p className="text-gray-600 mb-6 dark:text-gray-400">
            {t('featureSections.privacy.description')}
          </p>
          <div className="space-y-3 mb-12">
            {(Array.isArray(t('featureSections.privacy.features', { returnObjects: true }))
              ? t('featureSections.privacy.features', { returnObjects: true }) as string[]
              : [
                  "End-to-end encryption for all your data",
                  "Secure cross-device synchronization",
                  "Complete data control and deletion rights",
                ]
            ).map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-gray-700 text-sm dark:text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
          <a
            href={getStoreUrl(i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black text-white px-6 py-3 rounded-full hover:bg-gray-800 transition-colors"
          >
            {t('featureSections.downloadNow')}
          </a>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center gap-4 dark:bg-slate-800 dark:border-slate-700">
            <div className="bg-red-100 p-2 rounded-full dark:bg-red-900/30">
              <Ban className="w-6 h-6 text-red-600" />
            </div>
            <span className="text-gray-800 dark:text-gray-200">{t('featureSections.privacy.guarantees.neverSold')}</span>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center gap-4 dark:bg-slate-800 dark:border-slate-700">
            <div className="bg-blue-100 p-2 rounded-full dark:bg-blue-900/30">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-gray-800 dark:text-gray-200">{t('featureSections.privacy.guarantees.youControl')}</span>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center gap-4 dark:bg-slate-800 dark:border-slate-700">
            <div className="bg-gray-100 p-2 rounded-full dark:bg-slate-700">
              <Trash2 className="w-6 h-6 text-gray-600" />
            </div>
            <span className="text-gray-800 dark:text-gray-200">{t('featureSections.privacy.guarantees.permanentDeletion')}</span>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg flex items-center gap-4 dark:bg-slate-800 dark:border-slate-700">
            <div className="bg-yellow-100 p-2 rounded-full dark:bg-yellow-900/30">
              <Shield className="w-6 h-6 text-yellow-600" />
            </div>
            <span className="text-gray-800 dark:text-gray-200">{t('featureSections.privacy.guarantees.valuePrivacy')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureSections;